import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useModal } from '../contexts/ModalContext';

const StyledHeader = styled.header<{ isHidden: boolean }>`
  background-color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 1rem 0;
  transition: transform 0.3s ease;
  transform: ${props => props.isHidden ? 'translateY(-100%)' : 'translateY(0)'};
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const BrandText = styled.span`
  font-weight: bold;
  color: var(--primary);
  margin-left: 0.5rem;
  font-size: 1.2rem;
`;

const SubBrandText = styled.span`
  color: var(--secondary);
  margin-left: 0.5rem;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinks = styled.nav`
  display: flex;
  gap: 1.5rem;

  @media (max-width: 768px) {
    display: ${props => props.className === 'open' ? 'flex' : 'none'};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: white;
    padding: 1rem;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }
`;

const NavLink = styled(Link)`
  color: #333;
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: var(--primary);
    text-decoration: none;
  }
`;

const NavAnchor = styled.a`
  color: #333;
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: var(--primary);
    text-decoration: none;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const PasswordModal = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
`;

const PasswordContainer = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
`;

const PasswordTitle = styled.h3`
  margin-top: 0;
  color: var(--primary);
  font-size: 1.25rem;
  margin-bottom: 1rem;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
`;

const CancelButton = styled(Button)`
  background-color: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #1f2937;
  
  &:hover {
    background-color: #e5e7eb;
  }
`;

const SubmitButton = styled(Button)`
  background-color: var(--primary);
  border: 1px solid var(--primary);
  color: white;
  
  &:hover {
    background-color: #166534;
  }
`;

const Header: React.FC = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { isModalOpen } = useModal();
  const [clickCount, setClickCount] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const passwordTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Reset click count after 2 seconds of inactivity
  useEffect(() => {
    if (clickCount > 0 && clickCount < 5) {
      passwordTimeoutRef.current = setTimeout(() => {
        setClickCount(0);
      }, 2000);
    }
    
    return () => {
      if (passwordTimeoutRef.current) {
        clearTimeout(passwordTimeoutRef.current);
      }
    };
  }, [clickCount]);
  
  const handleBrandClick = () => {
    if (passwordTimeoutRef.current) {
      clearTimeout(passwordTimeoutRef.current);
    }
    
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount === 5) {
      setShowPasswordModal(true);
      setClickCount(0);
    }
  };
  
  const handlePasswordSubmit = () => {
    if (password === '627K000BIDI') {
      setShowPasswordModal(false);
      setPassword('');
      navigate('/admin');
    } else {
      setErrorMessage('Invalid password');
      setTimeout(() => setErrorMessage(''), 2000);
    }
  };

  return (
    <>
      <StyledHeader isHidden={isModalOpen}>
        <NavContainer>
        <LogoContainer>
          <Link to="/">
            <img
              src="/FocusKochiLogo.jpeg"
              alt="Wisdom Youth Logo"
              height="40"
            />
          </Link>
          <div>
            <BrandText onClick={handleBrandClick}>Wisdom Youth</BrandText>
            <SubBrandText>Professional Wing</SubBrandText>
          </div>
        </LogoContainer>

        <MenuButton onClick={() => setIsNavOpen(!isNavOpen)}>
          {isNavOpen ? '✕' : '☰'}
        </MenuButton>

        <NavLinks className={isNavOpen ? 'open' : ''}>
          <NavLink to="/">Home</NavLink>
          <NavAnchor href="#about">About</NavAnchor>
          <NavAnchor href="#donations">Donations</NavAnchor>
          <NavAnchor href="#contact">Contact</NavAnchor>
        </NavLinks>
        </NavContainer>
      </StyledHeader>
      
      <PasswordModal isVisible={showPasswordModal}>
        <PasswordContainer>
          <PasswordTitle>Administrator Access</PasswordTitle>
          {errorMessage && <p style={{ color: 'red', marginBottom: '1rem' }}>{errorMessage}</p>}
          <PasswordInput 
            type="password" 
            placeholder="Enter password" 
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
          />
          <ButtonContainer>
            <CancelButton onClick={() => {
              setShowPasswordModal(false);
              setPassword('');
            }}>
              Cancel
            </CancelButton>
            <SubmitButton onClick={handlePasswordSubmit}>
              Submit
            </SubmitButton>
          </ButtonContainer>
        </PasswordContainer>
      </PasswordModal>
    </>
  );
};

export default Header;
